import { cn, formatRank } from "@/lib/utils";
import { ChevronDown } from "lucide-react";
import { useState, type ReactNode } from "react";
import type { ChampionStats, Highlights, LpHistory, LpPoint, MatchHistory, PlayerRank } from "../../types";
import {
	ddFallback,
	getChampionIconUrl,
	getItemIconUrl,
	getQueueLabel,
	getRankImage,
	getRuneIconUrl,
	getRuneName,
	getSummonerSpellName,
	getSummonerSpellUrl,
	getTierColor,
	isApexTier
} from "../../utils/ddragon";
import { label } from "../ui/primitives";

export function formatDuration(seconds: number) {
	const m = Math.floor(seconds / 60);
	const s = seconds % 60;
	return `${m}:${s.toString().padStart(2, "0")}`;
}

export function formatRelativeTime(timestamp: string) {
	const then = new Date(timestamp).getTime();
	const minutes = Math.floor((Date.now() - then) / 60000);
	if (minutes < 1) return "Just now";
	if (minutes < 60) return `${minutes}m ago`;
	const hours = Math.floor(minutes / 60);
	if (hours < 24) return `${hours}h ago`;
	const days = Math.floor(hours / 24);
	if (days === 1) return "Yesterday";
	if (days < 7) return `${days}d ago`;
	return new Date(timestamp).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

// Riot's developer policy forbids shaming players by any metric: strong numbers get the signal colour, everything
// else is plain neutral text. There is no "bad" colour anywhere in this file, on purpose.
export const kdaColor = (kda: number) => (kda >= 4 ? "text-signal" : "text-ink-3");
export const winrateColor = (wr: number) => (wr >= 55 ? "text-signal" : "text-ink");

export const kdaOf = (k: number, d: number, a: number) => (d === 0 ? (k + a > 0 ? Infinity : 0) : (k + a) / d);
export const formatKda = (kda: number) => (kda === Infinity ? "Perfect" : kda.toFixed(2));

/** Short queue names for dense rows; the full label stays in aria-labels. */
const SHORT_QUEUE: Record<string, string> = { "Normal Draft": "Normal", "Normal Blind": "Blind", "Co-op vs AI": "Co-op" };
export const shortQueue = (queueId: number) => {
	const l = getQueueLabel(queueId);
	return SHORT_QUEUE[l] ?? l;
};

// ─── Collapsible section (rail cards) ────────────────────────────────────────

const isSmallScreen = () => typeof window !== "undefined" && window.matchMedia?.("(max-width: 767px)").matches;

/**
 * Card with a disclosure header. `mobileCollapsed` sections start closed on phones so the games list is one scroll away.
 * Height animates via grid-template-rows (0fr ↔ 1fr), which respects prefers-reduced-motion through the global rule.
 */
export function Section({ title, summary, mobileCollapsed = false, children }: { title: string; summary?: ReactNode; mobileCollapsed?: boolean; children: ReactNode }) {
	const [open, setOpen] = useState(() => !(mobileCollapsed && isSmallScreen()));
	const [bodyId] = useState(() => `sec-${Math.random().toString(36).slice(2, 8)}`);
	return (
		<section className="rounded-[10px] border border-line bg-surface w-full">
			<button
				type="button"
				onClick={() => setOpen(o => !o)}
				aria-expanded={open}
				aria-controls={bodyId}
				className="flex min-h-11 w-full cursor-pointer items-center gap-2.5 px-3.5 py-3 text-left"
			>
				<h2 className={cn(label, "m-0 flex-1")}>{title}</h2>
				{!open && summary}
				<ChevronDown aria-hidden="true" className={cn("h-4 w-4 text-ink-3 transition-transform duration-200", open && "rotate-180")} strokeWidth={2.3} />
			</button>
			<div id={bodyId} className={cn("grid transition-[grid-template-rows] duration-200 ease-out", open ? "grid-rows-[1fr]" : "grid-rows-[0fr]")} inert={!open}>
				<div className="min-h-0 overflow-hidden">{children}</div>
			</div>
		</section>
	);
}

// ─── Items ───────────────────────────────────────────────────────────────────

const ITEM_SIZE = { xs: "h-[19px] w-[19px] rounded-[3px]", sm: "h-[22px] w-[22px] rounded", md: "h-6 w-6 rounded-[5px]" } as const;

export function ItemIcon({ itemId, size = "md", isTrinket = false, gameVersion }: { itemId: number; size?: keyof typeof ITEM_SIZE; isTrinket?: boolean; gameVersion?: string }) {
	const cls = cn(ITEM_SIZE[size], "shrink-0 bg-raised", isTrinket && "rounded-full");
	if (!itemId) return <span aria-hidden="true" className={cn(cls, "block")} />;
	return <img src={getItemIconUrl(itemId, gameVersion)} onError={ddFallback} alt="" loading="lazy" className={cn(cls, "block object-cover")} />;
}

export function ItemRow({ items, size = "md", className, gameVersion }: { items: number[]; size?: keyof typeof ITEM_SIZE; className?: string; gameVersion?: string }) {
	return (
		<span className={cn("flex items-center gap-[3px]", className)}>
			{items.slice(0, 6).map((id, i) => (
				<ItemIcon key={i} itemId={id} size={size} gameVersion={gameVersion} />
			))}
			<span className="ml-[3px]">
				<ItemIcon itemId={items[6] ?? 0} size={size} isTrinket gameVersion={gameVersion} />
			</span>
		</span>
	);
}

function Spells({ ids, size = 22, gameVersion }: { ids: (number | null)[]; size?: number; gameVersion?: string }) {
	return (
		<span className="flex flex-col gap-0.5">
			{ids.map((id, i) =>
				id ? (
					<img key={i} src={getSummonerSpellUrl(id, gameVersion)} onError={ddFallback} alt={getSummonerSpellName(id)} title={getSummonerSpellName(id)} loading="lazy" style={{ width: size, height: size }} className="block rounded-[5px]" />
				) : (
					<span key={i} style={{ width: size, height: size }} className="block rounded-[5px] bg-raised" />
				)
			)}
		</span>
	);
}

// ─── Rank card ───────────────────────────────────────────────────────────────

/** LP as a 10-segment ruler: reads like a progress gauge, not a judgement. */
function LpRuler({ lp, color }: { lp: number; color: string }) {
	return (
		<div aria-hidden="true" className="my-[7px] grid grid-cols-10 gap-0.5">
			{Array.from({ length: 10 }, (_, i) => (
				<span key={i} className="block h-[5px] overflow-hidden rounded-[1px] bg-line">
					<span className="block h-full" style={{ width: `${Math.max(0, Math.min(1, (lp - i * 10) / 10)) * 100}%`, background: color }} />
				</span>
			))}
		</div>
	);
}

export function RankRow({ rank, label: queueLabel }: { rank: PlayerRank | null; label: string }) {
	if (!rank || rank.tier === "UNRANKED") {
		return (
			<div className="flex items-center gap-3.5">
				<img src={getRankImage("UNRANKED")} alt="" className="h-16 w-16 shrink-0 object-contain opacity-50" />
				<div>
					<div className="mb-0.5 text-[11px] text-ink-3">{queueLabel}</div>
					<div className="text-[15px] font-bold text-ink-2">Unranked</div>
				</div>
			</div>
		);
	}
	const colors = getTierColor(rank.tier);
	const total = rank.wins + rank.losses;
	const wr = total > 0 ? Math.round((rank.wins / total) * 100) : 0;
	const apex = isApexTier(rank.tier);
	return (
		<div className="flex items-center gap-3.5">
			<img src={getRankImage(rank.tier)} alt="" className="h-16 w-16 shrink-0 object-contain" />
			<div className="min-w-0 flex-1">
				<div className="mb-0.5 text-[11px] text-ink-3">{queueLabel}</div>
				<div className="flex items-baseline justify-between gap-2">
					<span className="text-[17px] font-extrabold [font-stretch:108%]">{apex ? formatRank(rank).replace(/ \d+ LP$/, "") : formatRank(rank)}</span>
					<span className="font-mono text-[13px] font-bold">{rank.leaguePoints} LP</span>
				</div>
				{apex ? <div className="h-3" /> : <LpRuler lp={rank.leaguePoints} color={colors.primary} />}
				<div className="flex justify-between text-xs text-ink-3">
					<span>
						{rank.wins}W {rank.losses}L
					</span>
					<span className={cn("font-bold", winrateColor(wr))}>{wr}%</span>
				</div>
			</div>
		</div>
	);
}

/** Kept for compatibility: one queue as its own card. The profile uses RanksCard (both queues in one section). */
export function RankCard({ rank, label: queueLabel }: { rank: PlayerRank | null; label: string }) {
	return (
		<div className="rounded-[10px] border border-line bg-surface p-3.5">
			<RankRow rank={rank} label={queueLabel} />
		</div>
	);
}

export function RanksCard({ solo, flex, premade }: { solo: PlayerRank | null; flex: PlayerRank | null; premade: PlayerRank | null }) {
	const summaryRank = solo && solo.tier !== "UNRANKED" ? solo : flex && flex.tier !== "UNRANKED" ? flex : null;
	return (
		<Section title="Ranked" summary={summaryRank ? <span className="text-[13px] font-semibold">{formatRank(summaryRank)}{isApexTier(summaryRank.tier) ? "" : ` · ${summaryRank.leaguePoints} LP`}</span> : undefined}>
			<div className="flex flex-col gap-3.5 px-3.5 pb-3.5">
				<RankRow rank={solo} label="Ranked Solo/Duo" />
				<RankRow rank={flex} label="Ranked Flex" />
				{premade && premade.tier !== "UNRANKED" && <RankRow rank={premade} label="Ranked 5s" />}
			</div>
		</Section>
	);
}

// ─── LP history ──────────────────────────────────────────────────────────────

const signed = (n: number) => `${n > 0 ? "+" : n < 0 ? "−" : "±"}${Math.abs(n)} LP`;
const day = (t: number) => new Date(t).toLocaleDateString(undefined, { month: "short", day: "numeric" });

/** Rank snapshots as a line. Solo/Duo if it has a history, else Flex; nothing until there are two points to connect. */
export function LpHistoryCard({ history }: { history: LpHistory }) {
	const [queueLabel, pts]: [string, LpPoint[]] = history.solo.length >= 2 ? ["Solo/Duo", history.solo] : ["Flex", history.flex];
	if (pts.length < 2) return null;
	const [W, H, PAD] = [300, 72, 4];
	const t0 = pts[0]!.t;
	const span = Math.max(1, pts[pts.length - 1]!.t - t0);
	const lo = Math.min(...pts.map(p => p.points));
	const hi = Math.max(...pts.map(p => p.points));
	const x = (p: LpPoint) => PAD + ((p.t - t0) / span) * (W - 2 * PAD);
	const y = (p: LpPoint) => H - PAD - ((p.points - lo) / Math.max(1, hi - lo)) * (H - 2 * PAD);
	const net = pts[pts.length - 1]!.points - pts[0]!.points;
	const last = pts[pts.length - 1]!;
	const summary = `${queueLabel} LP ${day(t0)} to ${day(last.t)}: ${signed(net)}`;
	return (
		<Section title="LP history" summary={<span className="text-[13px] font-semibold">{signed(net)}</span>}>
			<div className="px-3.5 pb-3.5">
				<svg role="img" aria-label={summary} viewBox={`0 0 ${W} ${H}`} className="block h-[72px] w-full overflow-visible">
					<polyline points={pts.map(p => `${x(p).toFixed(1)},${y(p).toFixed(1)}`).join(" ")} fill="none" stroke="var(--color-signal)" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
					<circle cx={x(last)} cy={y(last)} r="3" fill="var(--color-signal)" />
				</svg>
				<div className="mt-1.5 flex justify-between text-xs text-ink-3">
					<span>{day(t0)}</span>
					<span>
						{queueLabel} · {formatRank({ tier: last.tier, rank: last.rank, leaguePoints: last.lp, wins: 0, losses: 0 })}
					</span>
					<span>{day(last.t)}</span>
				</div>
				<p className="m-0 mt-1.5 text-[11px] text-ink-3">Tracked from the first time this profile was opened.</p>
			</div>
		</Section>
	);
}

// ─── Recent form ─────────────────────────────────────────────────────────────

/** Last N results as bars. Shape differs, not just colour: wins are filled, defeats are flat, remakes are outlined. */
export function RecentForm({ games }: { games: MatchHistory[] }) {
	if (games.length === 0) return null;
	const played = games.filter(g => !g.isRemake);
	const wins = played.filter(g => g.win).length;
	const losses = played.length - wins;
	const remakes = games.length - played.length;
	const wr = played.length ? Math.round((wins / played.length) * 100) : 0;
	const summary = `Recent form: ${wins} wins, ${losses} losses${remakes ? `, ${remakes} remakes` : ""} in the last ${games.length} games`;
	return (
		<div role="img" aria-label={summary}>
			<div className="mb-1.5 flex justify-between gap-3 text-xs text-ink-3">
				<span>Last {games.length}</span>
				<span>
					<span className="font-bold text-ink">{wins}W</span> {losses}L · <span className={cn("font-bold", winrateColor(wr))}>{wr}%</span>
				</span>
			</div>
			<div className="flex gap-0.5" aria-hidden="true">
				{[...games].reverse().map(g => (
					<span
						key={g.matchId}
						title={g.isRemake ? "Remake" : g.win ? "Win" : "Defeat"}
						className={cn("h-[18px] w-[7px] rounded-[2px]", g.isRemake ? "shadow-[inset_0_0_0_1px_var(--color-line-3)]" : g.win ? "bg-signal" : "bg-line-2")}
					/>
				))}
			</div>
		</div>
	);
}

// ─── Highlights ──────────────────────────────────────────────────────────────

export function HighlightsCard({ highlights }: { highlights: Highlights }) {
	const { multiKills, currentStreak, avgTeamDamagePercent, games } = highlights;
	const tiles: { value: string; label: string; accent: boolean }[] = [];
	if (currentStreak.type === "win" && currentStreak.count >= 3) tiles.push({ value: String(currentStreak.count), label: "Win streak", accent: true }); // hot streaks only
	if (multiKills.pentas > 0) tiles.push({ value: String(multiKills.pentas), label: multiKills.pentas > 1 ? "Penta kills" : "Penta kill", accent: true });
	if (multiKills.quadras > 0) tiles.push({ value: String(multiKills.quadras), label: multiKills.quadras > 1 ? "Quadra kills" : "Quadra kill", accent: true });
	if (multiKills.triples > 0) tiles.push({ value: String(multiKills.triples), label: multiKills.triples > 1 ? "Triple kills" : "Triple kill", accent: false });
	if (avgTeamDamagePercent !== null) tiles.push({ value: `${avgTeamDamagePercent.toFixed(1)}%`, label: "Avg team damage", accent: false });
	if (tiles.length === 0) return null;

	return (
		<Section title={`Highlights · last ${games}`} mobileCollapsed>
			<div className="grid grid-cols-2 gap-2 px-3.5 pb-3.5">
				{tiles.map(t => (
					<div key={t.label} className={cn("rounded-lg border p-2.5", t.accent ? "border-signal/25 bg-signal/[.07]" : "border-line bg-raised")}>
						<div className={cn("text-xl font-extrabold [font-stretch:112%]", t.accent ? "text-signal" : "text-ink")}>{t.value}</div>
						<div className="mt-px text-xs text-ink-2">{t.label}</div>
					</div>
				))}
			</div>
		</Section>
	);
}

// ─── Champions ───────────────────────────────────────────────────────────────

export function ChampionsCard({ champions, games }: { champions: ChampionStats[]; games: number }) {
	if (champions.length === 0) return null;
	const cols = "grid grid-cols-[36px_minmax(0,1fr)_auto_52px] items-center gap-2.5";
	return (
		<Section
			title={`Champions · last ${games}`}
			mobileCollapsed
			summary={
				<span className="flex gap-[3px]">
					{champions.slice(0, 3).map(c => (
						<img key={c.championId} src={getChampionIconUrl(c.championId)} alt={c.championName} className="h-[22px] w-[22px] rounded-[5px]" />
					))}
				</span>
			}
		>
			<div className="px-2 pb-2">
				<div aria-hidden="true" className={cn(cols, "px-1.5 pb-1.5 text-[10px] font-semibold uppercase tracking-[.08em] text-ink-3")}>
					<span />
					<span>Champion</span>
					<span className="text-right">KDA</span>
					<span className="text-right">Win %</span>
				</div>
				<ul className="m-0 list-none p-0">
					{champions.map(c => (
						<li key={c.championId} className={cn(cols, "rounded-lg p-1.5 hover:bg-raised")}>
							<img src={getChampionIconUrl(c.championId)} alt="" loading="lazy" className="h-9 w-9 rounded-[7px]" />
							<div className="min-w-0">
								<div className="truncate text-sm font-bold">{c.championName}</div>
								<div className="truncate text-xs text-ink-3">
									{c.gamesPlayed} {c.gamesPlayed === 1 ? "game" : "games"} · {c.csPerMin} CS/m
								</div>
							</div>
							<div className="text-right">
								<div className={cn("text-[13px] font-bold", c.kda >= 4 ? "text-signal" : "text-ink")}>{c.kda.toFixed(2)}</div>
								<div className="whitespace-nowrap text-[11px] text-ink-3">
									{c.avgKills} / {c.avgDeaths} / {c.avgAssists}
								</div>
							</div>
							<div className="text-right">
								<div className={cn("text-[13px] font-bold", winrateColor(c.winrate))}>{c.winrate}%</div>
								<div className="text-[11px] text-ink-3">
									{c.wins}W {c.losses}L
								</div>
							</div>
						</li>
					))}
				</ul>
			</div>
		</Section>
	);
}

// ─── Match row ───────────────────────────────────────────────────────────────

/**
 * One game. Two arrangements of the same content: a single dense line (md–lg and xl+, where the list is wide enough)
 * and a three-line stack (phones, and lg–xl where the rail sits beside the list).
 * Result is carried by the word (Victory / Defeat / Remake), the stripe shape and colour: never colour alone.
 */
export function MatchRow({ match, expanded, onToggle, controls }: { match: MatchHistory; expanded: boolean; onToggle: () => void; controls?: string }) {
	const kda = kdaOf(match.kills, match.deaths, match.assists);
	const cs = match.totalMinionsKilled + match.neutralMinionsKilled;
	const csMin = match.gameDuration ? (cs / (match.gameDuration / 60)).toFixed(1) : "0.0";
	const items = [match.item0, match.item1, match.item2, match.item3, match.item4, match.item5, match.item6];
	const remake = match.isRemake;
	const result = remake ? "Remake" : match.win ? "Victory" : "Defeat";
	const resultCls = remake ? "text-ink-3" : match.win ? "text-signal" : "text-ink-2";
	const kdaText = remake ? "—" : kda === Infinity ? "Perfect KDA" : `${kda.toFixed(2)} KDA`;
	const kdaCls = !remake && kda >= 4 ? "text-signal" : "text-ink-3";
	const ago = formatRelativeTime(match.timestamp);
	const queue = shortQueue(match.queueId);
	const lp = match.lpDelta === null || match.lpDelta === undefined ? null : signed(match.lpDelta);
	const lpCls = (match.lpDelta ?? 0) > 0 ? "text-signal" : "text-ink-3"; // no red for a loss
	const aria = `${result}${lp ? ` (${lp})` : ""}, ${match.championName}, ${match.kills} kills ${match.deaths} deaths ${match.assists} assists, ${cs} CS, ${getQueueLabel(match.queueId)}, ${ago}. ${expanded ? "Hide" : "Show"} scoreboard`;

	const champ = (size: number) => (
		<img src={getChampionIconUrl(match.championId, match.gameVersion)} onError={ddFallback} alt="" style={{ width: size, height: size }} className={cn("block rounded-[9px]", remake && "opacity-55")} />
	);
	const Kda = (
		<span className="block whitespace-nowrap text-[17px] font-extrabold tracking-[.01em] [font-stretch:108%]">
			{match.kills}
			<span className="font-medium text-line-3"> / </span>
			{match.deaths}
			<span className="font-medium text-line-3"> / </span>
			{match.assists}
		</span>
	);
	const chevron = <ChevronDown aria-hidden="true" className={cn("h-4 w-4 shrink-0 text-ink-2 transition-transform duration-200", expanded && "rotate-180")} strokeWidth={2.3} />;

	return (
		<button
			type="button"
			onClick={onToggle}
			aria-expanded={expanded}
			aria-controls={controls}
			aria-label={aria}
			className="flex w-full cursor-pointer items-stretch text-left transition-colors hover:bg-white/[.025]"
		>
			<span
				aria-hidden="true"
				className={cn(
					"w-1 shrink-0",
					remake ? "bg-[repeating-linear-gradient(180deg,var(--color-line-3)_0_4px,transparent_4px_8px)]" : match.win ? "bg-signal" : "bg-line-2"
				)}
			/>

			{/* single line */}
			<span className="hidden min-w-0 flex-1 grid-cols-[64px_84px_110px_minmax(88px,1fr)_80px_188px_52px] items-center gap-2.5 px-3 py-2.5 md:grid lg:hidden xl:grid">
				<span className="min-w-0">
					<span className={cn("block text-[13px] font-extrabold", resultCls)}>{result}</span>
					<span className="mt-0.5 block truncate text-xs text-ink-2">{queue}</span>
					<span className="mt-0.5 block font-mono text-[11px] text-ink-3">{formatDuration(match.gameDuration)}</span>
				</span>
				<span className={cn("text-left font-mono font-extrabold leading-none", lpCls)}>
					{lp && (
						<>
							<span className="flex items-baseline gap-1">
								<span className="text-[22px]">{lp.replace(" LP", "")}</span>
								<span className="text-[11px] tracking-wider">LP</span>
							</span>
						</>
					)}
				</span>
				<span className="flex items-center gap-1">
					{champ(48)}
					<Spells ids={[match.summoner1Id, match.summoner2Id]} gameVersion={match.gameVersion} />
					<span className="flex flex-col items-center gap-0.5">
						{match.primaryKeystone ? <img src={getRuneIconUrl(match.primaryKeystone)} alt={getRuneName(match.primaryKeystone)} title={getRuneName(match.primaryKeystone)} className="block h-[22px] w-[22px] rounded-full bg-[#07090d]" /> : <span className="h-[22px] w-[22px]" />}
						{match.secondaryRuneStyle ? <img src={getRuneIconUrl(match.secondaryRuneStyle)} alt={getRuneName(match.secondaryRuneStyle)} className="block h-4 w-4 p-0.5" /> : null}
					</span>
				</span>
				<span className="min-w-0">
					{Kda}
					<span className={cn("mt-0.5 block text-xs font-bold", kdaCls)}>{kdaText}</span>
				</span>
				<span className="text-xs leading-normal text-ink-3">
					<span className="block">
						<span className="font-bold text-ink">{cs}</span> CS {csMin}/m
					</span>
					<span className="block">
						<span className="font-bold text-ink">{match.visionScore}</span> vision
					</span>
				</span>
				<ItemRow items={items} gameVersion={match.gameVersion} />
				<span className="flex flex-col items-end gap-1.5 text-xs text-ink-3">
					<span className="whitespace-nowrap">{ago}</span>
					{chevron}
				</span>
			</span>

			{/* stacked */}
			<span className="flex min-w-0 flex-1 flex-col gap-2 px-3 py-2.5 md:hidden lg:flex xl:hidden">
				<span className="flex min-w-0 items-center gap-2 text-xs text-ink-3">
					<span className={cn("font-extrabold", resultCls)}>{result}</span>
					<span className="truncate text-ink-2">{queue}</span>
					<span className="font-mono">{formatDuration(match.gameDuration)}</span>
					{lp && <span className={cn("font-mono font-bold", lpCls)}>{lp}</span>}
					<span className="ml-auto whitespace-nowrap">{ago}</span>
				</span>
				<span className="flex items-center gap-2.5">
					<span className="relative shrink-0">
						{champ(44)}
						{match.primaryKeystone ? (
							<img src={getRuneIconUrl(match.primaryKeystone)} alt={getRuneName(match.primaryKeystone)} className="absolute -right-[5px] -bottom-[5px] h-5 w-5 rounded-full bg-[#07090d] shadow-[0_0_0_2px_var(--color-surface)]" />
						) : null}
					</span>
					<Spells ids={[match.summoner1Id, match.summoner2Id]} size={20} gameVersion={match.gameVersion} />
					<span className="min-w-0 flex-1">
						{Kda}
						<span className={cn("block text-xs font-bold", kdaCls)}>{kdaText}</span>
					</span>
					<span className="text-right text-xs text-ink-3">
						<span className="block">
							<span className="font-bold text-ink">{cs}</span> CS
						</span>
						<span className="block">{csMin}/m</span>
					</span>
					{chevron}
				</span>
				<span className="flex items-center">
					<ItemRow items={items} size="sm" gameVersion={match.gameVersion} />
					<span className="ml-auto text-xs text-ink-3">
						<span className="font-bold text-ink">{match.visionScore}</span> vision
					</span>
				</span>
			</span>
		</button>
	);
}

/** Row container: tint and border only. Victory gets a faint signal tint; defeat stays the plain surface. */
export function matchCardClass(match: MatchHistory, open: boolean) {
	return cn(
		"overflow-hidden rounded-[10px] border transition-colors",
		match.win && !match.isRemake ? "bg-win-row" : "bg-surface",
		open ? "border-line-3" : match.win && !match.isRemake ? "border-win-line" : "border-line"
	);
}

export function MatchRowSkeleton() {
	return <div aria-hidden="true" className="h-32 rounded-[10px] border border-line bg-surface animate-skeleton md:h-[70px] lg:h-32 xl:h-[70px]" />;
}
